/**
 * Service-worker update channel — the "a new version is ready" signal, and the
 * one place that applies it.
 *
 * WHY THIS EXISTS (observed 2026-07-27, live on a Pixel 7 + reproduced in
 * Chrome via scripts/pw/drive.mjs): the SW precaches `index.html` and serves it
 * CACHE-FIRST, so the HTML naming the hashed bundle comes from the SW, not the
 * network. The old SW therefore keeps serving the old bundle until a NEW
 * navigation happens. An installed PWA resumed from the launcher performs NO
 * navigation, so the update installs into the precache and then sits there
 * unused — indefinitely. Measured directly: after a resume the page was still
 * running `index-CKXJQMVd.js` while the precache already held
 * `index-CAlgoSDl.js`. Clearing app storage was the only escape, which is not
 * something a superintendent in the field can be asked to do.
 *
 * THE MODEL (Red's call 2026-07-27): the new SW is left in `waiting` — the SW
 * no longer calls `skipWaiting()` on install — and the app surfaces an explicit
 * affordance. Nothing moves under the user mid-task; the update applies only on
 * a deliberate tap. That tap tells the waiting SW to take over, and the
 * resulting `controllerchange` triggers exactly one reload, which is the
 * navigation that finally serves the new HTML.
 *
 * Deliberately NOT auto-reloading on `controllerchange` in general: a reload
 * firing during the resume tick is what caused the "wonky scroll" 100dvh
 * mis-layout documented in main.tsx, and mid-capture it would cost the super
 * their recording. We reload only when WE initiated the swap.
 */

type Listener = (ready: boolean) => void;

let listeners: Listener[] = [];
let ready = false;
/** Set only by applyUpdate(), so a controllerchange we did NOT ask for (e.g. an
 * update applied in another tab) never yanks the page out from under the user. */
let applying = false;
let controllerWatched = false;
let applicationTimer: ReturnType<typeof setTimeout> | null = null;
let settleApplication: ((activated: boolean) => void) | null = null;
let applicationPromise: Promise<boolean> | null = null;

function emit() {
  for (const cb of listeners) cb(ready);
}

function setReady(value: boolean): void {
  if (ready === value) return;
  ready = value;
  emit();
}

/** Subscribe to "an update is waiting". Fires immediately with current state. */
export function onUpdateReady(cb: Listener): () => void {
  listeners.push(cb);
  cb(ready);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
}

/** Test seam + internal: flip the signal on. */
export function markUpdateReady(): void {
  setReady(true);
}

export function isUpdateReady(): boolean {
  return ready;
}

export type UpdateCheckResult = "ready" | "current" | "activating" | "unsupported" | "failed";

/** User-initiated network check. The registration watcher remains the authority
 * if an installing worker reaches `installed` shortly after this call returns. */
export async function checkForUpdate(): Promise<UpdateCheckResult> {
  const container = navigator.serviceWorker;
  if (!container?.getRegistration) return "unsupported";
  if (applying) return "activating";
  try {
    const reg = await container.getRegistration();
    if (!reg) return "unsupported";
    watchRegistration(reg);
    await reg.update();
    if (reg.waiting) {
      markUpdateReady();
      return "ready";
    }
    if (reg.installing || reg.active?.state === "activating") {
      setReady(false);
      return "activating";
    }
    setReady(false);
    return "current";
  } catch {
    return "failed";
  }
}

/**
 * Wire a registration up to the channel. An update is only announced when a
 * controller ALREADY exists — on a first-ever install there is no previous
 * version to update FROM, and prompting there would be nonsense.
 */
export function watchRegistration(reg: ServiceWorkerRegistration): void {
  const controlled = () => Boolean(navigator.serviceWorker?.controller);

  // Already sitting in waiting when we registered (e.g. installed during a
  // previous run that the user never applied).
  if (reg.waiting && controlled()) markUpdateReady();

  reg.addEventListener("updatefound", () => {
    const incoming = reg.installing;
    if (!incoming) return;
    incoming.addEventListener("statechange", () => {
      // "installed" + an existing controller == a genuine update now waiting.
      if (incoming.state === "installed" && controlled()) markUpdateReady();
    });
  });

  if (!controllerWatched) {
    controllerWatched = true;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const initiatedHere = applying;
      applying = false;
      setReady(false);
      settleApplication?.(true);
      settleApplication = null;
      if (applicationTimer !== null) clearTimeout(applicationTimer);
      applicationTimer = null;
      if (!initiatedHere) return; // not our swap — leave the page alone
      window.location.reload();
    });
  }
}

/**
 * Apply the waiting update: tell it to take over, then let the controllerchange
 * handler above do the single reload. No-ops when nothing is waiting.
 */
export function applyUpdate(timeoutMs = 8_000): Promise<boolean> {
  if (applicationPromise) return applicationPromise;

  const request = (async (): Promise<boolean> => {
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      if (!reg?.waiting) {
        // A previous local request may already have moved this worker into
        // activation. Do not surrender ownership of its controllerchange.
        if (!applying) setReady(false);
        return false;
      }
      watchRegistration(reg);
      applying = true;
      return new Promise<boolean>((resolve) => {
        settleApplication = resolve;
        applicationTimer = setTimeout(() => {
          const stillWaiting = Boolean(reg.waiting);
          // Keep ownership even when the worker has not processed SKIP_WAITING yet.
          // The message can be serviced after this UI timeout; only the eventual
          // controllerchange can prove this locally initiated swap is complete.
          settleApplication = null;
          applicationTimer = null;
          setReady(Boolean(stillWaiting && navigator.serviceWorker?.controller));
          resolve(false);
        }, timeoutMs);
        try {
          reg.waiting?.postMessage({ type: "SKIP_WAITING" });
        } catch {
          applying = false;
          settleApplication = null;
          if (applicationTimer !== null) clearTimeout(applicationTimer);
          applicationTimer = null;
          setReady(Boolean(reg.waiting && navigator.serviceWorker?.controller));
          resolve(false);
        }
      });
    } catch {
      applying = false;
      return false;
    }
  })();
  applicationPromise = request;
  void request.finally(() => {
    if (applicationPromise === request) applicationPromise = null;
  });
  return request;
}

/** Test-only reset. */
export function __resetUpdateChannel(): void {
  listeners = [];
  ready = false;
  applying = false;
  controllerWatched = false;
  settleApplication = null;
  applicationPromise = null;
  if (applicationTimer !== null) clearTimeout(applicationTimer);
  applicationTimer = null;
}
